import { AxisCode, BRAND_KIT_FILE_TYPES, type ObjectTag } from '@atlas/types'
import { deriveUploadProgress } from '@atlas/ui-components'
import type { AssetListAsset, AssetListRole } from '@atlas/ui-domain'
import { humanizeCode } from '@/lib/labels'
import type { FileWithTagsDTO } from '@/services/file.service'
import { isConfirmedStatus, isPreUploadFailure, UploadItemStatus } from '@/upload/upload-status'
import type { UploadItem } from '@/upload/upload-store'

/** The canonical kit order; a `file_type` outside it sorts after, alphabetically. */
const KIT_ORDER: string[] = [...BRAND_KIT_FILE_TYPES]

/**
 * The group a file carrying no `file_type` at all falls into. Its code cannot collide with a
 * `file_type` value: every axis value is lowercase snake_case.
 *
 * The code is exported because a consumer that turns roles into something OTHER than tiles -
 * the distribution package, which turns them into archive folders - has to recognise the one
 * role that names no axis value and give it a name of its own.
 */
export const OTHER_ROLE_CODE = '__other__'

const OTHER_ROLE = { code: OTHER_ROLE_CODE, label: 'Other files' }

const rankOf = (code: string) => {
	// The fallback sorts last, after even an unrecognised file_type: it is the group for files
	// that state nothing about themselves, and it must not lead a list.
	if (code === OTHER_ROLE.code) return KIT_ORDER.length + 1
	const index = KIT_ORDER.indexOf(code)
	return index === -1 ? KIT_ORDER.length : index
}

/**
 * The axes that tell two files in the SAME role apart, in the order they read.
 *
 * Locale leads because a role's repeats are most often one per market. The rest follow the order
 * a reader would ask them in: where it applies, what animal it is for, where it is published,
 * and who it is for. Everything else a kit file carries - the brand, the `file_type` that put
 * it in its group - is shared by every tile there and so distinguishes nothing.
 *
 * Only DIMENSION axes belong here: a tile reads its qualifiers off the tags the file carries,
 * and an ATTRIBUTE axis such as `aspect_ratio` is stored as an object attribute rather than a
 * tag, so listing one would silently spend a qualifier slot on a value that can never match.
 */
const DISTINGUISHING_AXES: string[] = [
	AxisCode.LOCALE,
	AxisCode.GEOGRAPHY,
	AxisCode.SPECIES,
	AxisCode.CHANNEL,
	AxisCode.AUDIENCE,
]

/**
 * A tag's display value: its resolved label, falling back to its code (which is what makes a
 * locale read as `es-ES`).
 *
 * A tag whose label is ABSENT rather than empty falls back the same way. `label` is typed as
 * present, but a tag reaches here from whatever the read returned, and a qualifier no one
 * resolved a label for is a value to print as its code — not a reason for the whole list to
 * fail to render.
 */
const valueOf = (tag: ObjectTag): string => (tag.label ?? '').trim() || tag.valueCode.trim()

/**
 * The values that separate a file from its siblings in the same role, in axis order.
 *
 * At most one value per axis is taken - a file tagged for two locales contributes the first,
 * because which one it belongs to is a classification question and joining them would overstate
 * what one tile can say. How many of these actually fit on a tile is the tile's decision.
 */
const qualifiersOf = (tags: ObjectTag[]): string[] =>
	DISTINGUISHING_AXES.map((axisCode) => tags.find((tag) => tag.axisCode === axisCode))
		.filter((tag): tag is ObjectTag => tag !== undefined)
		.map(valueOf)
		.filter((value) => value !== '')

/**
 * One file as the asset-list card renders it: display-ready, with the values that separate it
 * from its siblings in the same role already resolved.
 *
 * Exported because a host that builds its OWN roles - the work order's Guidelines tab, which
 * states every canonical kit role whether or not the order resolves a file for it - still has
 * to describe its files the same way. A second copy of this conversion is how one surface
 * ends up spending a qualifier slot on an axis every tile in the group shares.
 */
export const toAssetListAsset = (
	file: FileWithTagsDTO,
	thumbnails: Record<string, string | null> = {},
): AssetListAsset => ({
	id: file.fileId,
	fileName: file.fileName,
	contentType: file.contentType,
	fileSize: file.fileSize,
	qualifiers: qualifiersOf(file.tags),
	previewUrl: thumbnails[file.fileId] ?? null,
})

/**
 * Files split by `file_type`, kit roles first in their canonical order, as the roles
 * `AssetListCard` renders.
 *
 * Grouped on the tags the files actually carry, so no group can appear with nothing behind it -
 * and none is fabricated for a role nothing was supplied for. A file carrying two file types is
 * listed under both: it serves both roles, and picking one would misreport the other.
 *
 * A ROLE HOLDS EACH FILE AT MOST ONCE, keyed by file id. `tags` is flattened across every tuple a
 * file carries, so a file asserted once per region repeats the same `file_type` once per tuple -
 * five per-market assertions about one logo arrived as five identical `logo` tags, and the group
 * rendered "LOGO · 5" for a single file: a count of assertions wearing the clothes of a count of
 * files. The repeats are correct data (each tuple is a true statement about where that logo
 * applies); only the read had to stop mistaking them for separate files.
 *
 * Deduped on the FILE rather than on the tags, because that is the invariant the group actually
 * has to hold - one tile per file. Collapsing repeated tags instead would fix only the case where
 * the repeats happen to be identical, and would still admit a file twice the moment two tuples
 * differ in some other axis while agreeing on `file_type`.
 *
 * A file carrying NO `file_type` lands in "Other files" rather than being dropped. The brand
 * page lists everything classified to a brand, where an unclassified file silently vanishing
 * would understate the inventory; a read already filtered to `file_type` values - the product
 * page's brand kit - can never reach this group.
 */
export const toFileTypeRoles = (
	files: FileWithTagsDTO[],
	thumbnails: Record<string, string | null> = {},
): AssetListRole[] => {
	// Each role's assets keyed by file id, so adding the same file twice is a no-op rather than a
	// second tile. The first add wins, which keeps the arrival order the roles are read in.
	const byCode = new Map<string, { role: AssetListRole; assetIds: Set<string> }>()
	const addTo = (code: string, label: string, file: FileWithTagsDTO): void => {
		const entry = byCode.get(code) ?? { role: { code, label, assets: [] }, assetIds: new Set<string>() }
		byCode.set(code, entry)
		if (entry.assetIds.has(file.fileId)) return
		entry.assetIds.add(file.fileId)
		entry.role.assets.push(toAssetListAsset(file, thumbnails))
	}

	for (const file of files) {
		const fileTypes = file.tags.filter((t) => t.axisCode === AxisCode.FILE_TYPE)
		if (fileTypes.length === 0) {
			addTo(OTHER_ROLE.code, OTHER_ROLE.label, file)
			continue
		}
		for (const tag of fileTypes) {
			addTo(tag.valueCode, tag.label || humanizeCode(tag.valueCode), file)
		}
	}
	return [...byCode.values()]
		.map((entry) => entry.role)
		.sort((a, b) => rankOf(a.code) - rankOf(b.code) || a.label.localeCompare(b.label))
}

/**
 * Is this upload still the only tile its file has in the group?
 *
 * The placeholder is handed over by the ARRIVAL of the real tile, never by the upload's own
 * completion. The two are not the same moment: classification finishes, the read is
 * invalidated, and the refetch lands some time later - so a placeholder retired on terminal
 * status leaves the cell empty until then, and one retired only on `SETTLED` sits beside the
 * real tile for as long as the remaining concerns take. Keying on the file being listed makes
 * the swap a single render either way. The ceiling of keying on the NAME: importing a file
 * whose name the group already lists retires the placeholder on the render it appears, so
 * that upload shows no progress at all.
 *
 * The exceptions are the outcomes where no tile is ever coming: a failure before anything was
 * stored, and a file that finished with a failed concern - if that concern was the
 * classification, the file is stored but reaches no group, so waiting for it would wait
 * forever. Both are reported through the notification surface, and the host keeps its own
 * tile below the card, where the failed concern is retryable.
 */
const isImporting = (item: UploadItem, listedNames: Set<string>): boolean => {
	if (isPreUploadFailure(item.status)) return false
	if (!isConfirmedStatus(item.status)) return true
	return item.status !== UploadItemStatus.FINISHED_WITH_ERRORS && !listedNames.has(item.fileName)
}

/**
 * An upload still in flight, as a tile in the group it was dropped on.
 *
 * An import reaches its group only once the file is stored AND classified by a worker, which
 * is two round trips and a queue after the drop - long enough that the group sat unchanged
 * and the import read as ignored. The placeholder holds the cell it will occupy and shows how
 * far the transfer has got, on the same bar semantics as every other upload tile: full at
 * confirm, because the file is stored by then and the concerns are ours, not the user's.
 */
const toUploadingAsset = (item: UploadItem): AssetListAsset => ({
	id: item.id,
	fileName: item.fileName,
	contentType: item.contentType,
	fileSize: item.fileSize,
	qualifiers: [],
	uploadProgress: deriveUploadProgress({
		uploadProgress: item.progress,
		isConfirming: item.status === UploadItemStatus.CONFIRMING,
		isConfirmed: isConfirmedStatus(item.status),
		concerns: item.concerns,
	}).percent,
})

/**
 * A role with its host's in-flight imports appended as placeholder tiles.
 *
 * Shared rather than per-surface: every card that imports into a group waits the same two
 * round trips and a queue for the file to come back through its own read, and a surface that
 * skipped the placeholder would read as having ignored the drop for those seconds.
 */
export const withUploadingAssets = (role: AssetListRole, items: UploadItem[], owner: string): AssetListRole => {
	const listedNames = new Set(role.assets.map((asset) => asset.fileName))
	const importing = items.filter((item) => item.owner === owner && isImporting(item, listedNames))
	return importing.length ? { ...role, assets: [...role.assets, ...importing.map(toUploadingAsset)] } : role
}
